import { useContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import railsPostFetch from '@aytm/lib/common/rails-post-fetch'

import { DataContext } from '@aytm/app/stats/page/sidebar/reports/ReportsPanel'

import { EMAIL_REGEX } from '@aytm/app/lib/regex'

import copyToClipboard from '@aytm/lib/common/copy-to-clipboard'

import { VIRTUAL_REPORTS_IDS, updateItemShareLink } from '@aytm/app/stats/page/sidebar/reports/actions'
import { isSkipperReport, isSkipperGuidedReport } from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReport'

// components
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// styles
import style from '@aytm/app/stats/page/sidebar/reports/styles/ReportPopup.module.scss'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

const EMAILS_MAX = 10

const parseEmails = str =>
  str
    .replace(/[\s,:;]/g, ' ')
    .split(' ')
    .map(v => v.trim())
    .filter(v => 0 < v.length)

const selectValue = event => {
  event.target.parentElement.querySelector('input').select()
}

let emailsTimer = null

const ReportPopupSharing = ({ model }) => {
  const { state, mode, listModel, dispatch, isSurveyGuest, isSurveyViewer, onReportsChange, onReportsError } =
    useContext(DataContext)

  const modelState = state[model.id] || {}

  const shareLinkExist = model.current.shareLink && 0 < model.current.shareLink.length
  // A report that cannot be deleted keeps its link too: a protected one, a concept report and the
  // auto-launched Skipper report.
  const isLinkKept =
    true === model.current.protected ||
    'concept' == model.current.type ||
    (isSkipperReport(model.current) && !isSkipperGuidedReport(model.current))

  const [copied, setCopied] = useState(false)

  const [emailsState, setEmailsState] = useState({
    emails: '',
    emailsReady: true,
    emailsSuccess: false,
    emailsError: false,
    emailsSentCount: 0,
    emailsInvalid: '',
    emailsChanged: false
  })

  const getModelProps = () => {
    return {
      url: PageGlobals.STAT_REPORT_URL,
      listModel,
      model,
      onReportsChange,
      onReportsError
    }
  }

  const onChange = event => {
    if (emailsTimer) clearTimeout(emailsTimer)
    setEmailsState({
      emails: event.target.value,
      emailsReady: 0 < event.target.value.length,
      emailsSuccess: false,
      emailsError: false,
      emailsSentCount: 0,
      emailsInvalid: '',
      emailsChanged: true
    })
  }

  const tickToHideEmails = () => {
    if (emailsTimer) clearTimeout(emailsTimer)
    emailsTimer = setTimeout(() => {
      setEmailsState(
        Object.assign({}, emailsState, {
          emailsSuccess: false,
          emailsError: false,
          emailsSentCount: 0,
          emailsInvalid: ''
        })
      )
    }, 15000)
  }

  const checkEmails = () => {
    const emails = parseEmails(emailsState.emails)
    const valid = 0 < emails.length && emails.length <= EMAILS_MAX && emails.every(v => null !== v.match(EMAIL_REGEX))
    return valid
  }

  const sendEmails = () => {
    if (checkEmails()) {
      dispatch({
        type: 'UPDATE_REPORT_STATE',
        request: {
          id: model.id,
          process: 'send'
        }
      })
      railsPostFetch(PageGlobals.STAT_REPORT_URL, {
        body: JSON.stringify({ do: 'email', id: model.id, emails: emailsState.emails })
      })
        .then(response => response.json())
        .then(data => {
          dispatch({
            type: 'UPDATE_REPORT_STATE',
            request: {
              id: model.id,
              process: null
            }
          })
          if (data.status) {
            setEmailsState(
              Object.assign({}, emailsState, {
                emailsSuccess: true,
                emailsError: false,
                emailsSentCount: Object.hasOwn(data, 'sent_count')
                  ? data.sent_count || 0
                  : parseEmails(emailsState.emails).length || 0,
                emailsInvalid: Object.hasOwn(data, 'invalid_emails') ? data.invalid_emails.join(', ') : '',
                emailsChanged: false
              })
            )
          } else {
            setEmailsState(Object.assign({}, emailsState, { emailsError: true }))
          }
          tickToHideEmails()
        })
        .catch(() => {
          dispatch({
            type: 'UPDATE_REPORT_STATE',
            request: {
              id: model.id,
              process: null
            }
          })

          tickToHideEmails()
        })
    } else setEmailsState(Object.assign({}, emailsState, { emailsReady: false }))
  }

  const updateShareLink = share => {
    updateItemShareLink(dispatch, {
      ...getModelProps(),
      share: share
    })
  }

  const copyLink = () => {
    copyToClipboard(model.current.shareLink)

    setCopied(true)
    setTimeout(() => setCopied(false), 1000)
  }

  // TODO
  const isProcessing = Object.hasOwn(modelState, 'process')
  const isDeleting = 'delete' == modelState.status
  const isDisabled =
    !emailsState.emails.length ||
    !shareLinkExist ||
    !emailsState.emailsReady ||
    isProcessing ||
    isSurveyGuest ||
    isSurveyViewer

  useEffect(
    () => () => {
      if (emailsTimer) clearTimeout(emailsTimer)
    },
    []
  )

  return (
    <>
      <div className={`${style['options-row']} ${style['options-row-title']}`}>Sharing options</div>

      {isDeleting ? null : (
        <div data-testid="stat-reports-report-popup-invite" className={style['options-section']}>
          <div className={style['options-row']}>
            <div className={style['options-row-label']}>
              <b>By invitation:</b> share report by email (enter up to 10 emails below)
            </div>
          </div>
          <div className={style['options-row']}>
            <div className={`${style['options-row-input']} p-fluid p-col`}>
              <span className={style['options-row-input-icon']}>
                <i className="icon-aytmfd-email" />
              </span>
              <InputText
                data-testid="stat-reports-report-popup-invite-input"
                className={classNames('p-inputtext-sm p-inputtext-rounded', {
                  'p-invalid': 0 < emailsState.emails.length && emailsState.emailsChanged && !emailsState.emailsReady
                })}
                placeholder="Share report by email"
                value={emailsState.emails}
                disabled={VIRTUAL_REPORTS_IDS.includes(model.current.type) || isSurveyGuest || isSurveyViewer}
                onChange={
                  VIRTUAL_REPORTS_IDS.includes(model.current.type) || isSurveyGuest || isSurveyViewer ? null : onChange
                }
              />
            </div>
            <div className={`${style['options-row-button']} p-fluid`}>
              <Button
                data-testid="stat-reports-report-popup-invite-button"
                className="p-button-sm p-button-rounded p-button-blue p-button-secondary"
                label="Invite"
                loading={isProcessing && 'send' == modelState.process}
                disabled={VIRTUAL_REPORTS_IDS.includes(model.current.type) || isDisabled}
                onClick={VIRTUAL_REPORTS_IDS.includes(model.current.type) || isDisabled ? null : sendEmails}
              />
            </div>
          </div>
          {emailsState.emailsSuccess ? (
            <div className={style['options-row']}>
              <div className={`${style['options-row-text']} ${style['options-row-success']}`}>
                {0 == emailsState.emailsSentCount ? 'No emails sent' : null}
                {1 == emailsState.emailsSentCount ? 'Email was sent' : null}
                {1 < emailsState.emailsSentCount ? 'Emails were sent' : null}
                {'' != emailsState.emailsInvalid && 0 < emailsState.emailsSentCount ? ', except' : null}
                {'' != emailsState.emailsInvalid && 0 < emailsState.emailsSentCount ? (
                  <span className={style['options-row-error']}>{emailsState.emailsInvalid}</span>
                ) : null}
              </div>
            </div>
          ) : null}
          {emailsState.emailsError ? (
            <div className={style['options-row']}>
              <div className={`${style['options-row-text']} ${style['options-row-error']}`}>
                Something went wrong, please try again later
              </div>
            </div>
          ) : null}
        </div>
      )}

      <div data-testid="stat-reports-report-popup-share-link" className={style['options-section']}>
        <div className={style['options-row']}>
          <div className={style['options-row-label']}>
            <b id={`stat-reports-report-${model.id}-popup-share-link-label`}>Report link</b> (Anyone with the link can
            view this report)
          </div>
        </div>
        <div className={style['options-row']}>
          <div className={`${style['options-row-input']} p-fluid p-col`}>
            <span className={style['options-row-input-icon']}>
              <i className="icon-aytmfd-link-v2" />
            </span>
            <InputText
              data-testid="stat-reports-report-popup-share-link-input"
              className="p-inputtext-sm p-inputtext-rounded"
              aria-labelledby={`stat-reports-report-${model.id}-popup-share-link-label`}
              value={shareLinkExist ? `${model.current.shareLink}${('charts' != mode && `#${mode}`) || ''}` : ''}
              disabled
              onFocus={selectValue}
            />
            {isProcessing && 'unshare' == modelState.process ? null : shareLinkExist &&
              !isLinkKept &&
              !isSurveyGuest &&
              !isSurveyViewer ? (
              <span
                data-testid="stat-reports-report-popup-share-link-delete-button"
                className={style['options-row-input-delete']}
              >
                <i
                  className="icon-aytmfd-x-v2"
                  aria-label="Delete report link"
                  {...pressable(() => updateShareLink(false))}
                />
              </span>
            ) : null}
          </div>
          <div className={`${style['options-row-button']} p-fluid`}>
            {!(shareLinkExist || isSurveyGuest || isSurveyViewer) && (
              <Button
                data-testid="stat-reports-report-popup-share-link-update-button"
                className="p-button-sm p-button-rounded p-button-blue"
                label="Generate"
                loading={isProcessing && 'share' == modelState.process}
                disabled={isProcessing}
                onClick={isProcessing ? null : () => updateShareLink(true)}
              />
            )}
            {shareLinkExist && (
              <Button
                data-testid="stat-reports-report-popup-share-link-copy-button"
                className="p-button-sm p-button-rounded p-button-blue"
                label={copied ? 'Copied!' : 'Copy'}
                disabled={isProcessing}
                onClick={isProcessing ? null : copyLink}
              />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

ReportPopupSharing.propTypes = {
  model: PropTypes.object.isRequired
}

ReportPopupSharing.displayName = 'ReportPopupSharing'

export default ReportPopupSharing
